import React, { FC, useRef, useState, useEffect } from 'react'
import { Link } from 'gatsby-plugin-react-i18next'
import logoLight from 'src/images/svg/logo-full-light.svg'

interface IAnniversaryTickerProps {
  className?: string
  text?: string
  to?: string
}

export const AnniversaryTicker: FC<IAnniversaryTickerProps> = ({
  className = '',
  text = '20 years of mamf',
  to = '/',
}) => {
  const trackRef = useRef<HTMLDivElement>(null)
  const setRef = useRef<HTMLDivElement>(null)
  const xRef = useRef(0)
  const isHoveredRef = useRef(false)
  const currentSpeedRef = useRef(35)
  const [setWidth, setSetWidth] = useState(0)

  const items = Array.from({ length: 8 })

  useEffect(() => {
    const measure = () => {
      if (setRef.current) {
        setSetWidth(setRef.current.getBoundingClientRect().width)
      }
    }
    measure()
    const timer = setTimeout(measure, 100)
    let ro: ResizeObserver | null = null
    if (typeof ResizeObserver !== 'undefined' && setRef.current) {
      ro = new ResizeObserver(measure)
      ro.observe(setRef.current)
    }
    window.addEventListener('resize', measure)
    return () => {
      clearTimeout(timer)
      if (ro) ro.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [])

  useEffect(() => {
    let animId: number
    let lastTime = performance.now()

    const animate = (time: number) => {
      const delta = time - lastTime
      lastTime = time

      if (setWidth > 0 && trackRef.current) {
        // Normal speed: 35px/s (slower). On hover: 10px/s (slows down without stopping)
        const targetSpeed = isHoveredRef.current ? 10 : 35
        currentSpeedRef.current += (targetSpeed - currentSpeedRef.current) * Math.min(1, (delta / 1000) * 6)
        const distance = (currentSpeedRef.current * delta) / 1000

        xRef.current -= distance
        if (xRef.current <= -setWidth) {
          xRef.current += setWidth
        }

        trackRef.current.style.transform = `translate3d(${xRef.current}px, 0, 0)`
      }

      animId = requestAnimationFrame(animate)
    }

    animId = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(animId)
  }, [setWidth])

  const renderContent = (ref?: React.RefObject<HTMLDivElement>) => (
    <div ref={ref} className="flex items-center shrink-0">
      {items.map((_, idx) => (
        <div key={idx} className="inline-flex items-center gap-4 md:gap-6 mx-4 md:mx-6 shrink-0">
          <span className="font-palanquinDark font-bold uppercase tracking-widest text-xs md:text-sm text-on-primary-unstable whitespace-nowrap">
            {text}
          </span>
          <img
            src={logoLight}
            alt="MAMF"
            className="h-5 md:h-6 w-auto object-contain shrink-0"
          />
          <span className="w-1.5 h-1.5 rounded-full bg-on-primary-unstable/60 shrink-0" aria-hidden="true" />
        </div>
      ))}
    </div>
  )

  return (
    <Link
      to={to}
      className={`block w-full bg-primary text-on-primary-unstable py-2 md:py-2.5 overflow-hidden select-none border-y border-white/10 cursor-pointer transition-opacity hover:opacity-95 ${className}`}
      role="marquee"
      aria-label={`${text} ticker`}
      onMouseEnter={() => {
        isHoveredRef.current = true
      }}
      onMouseLeave={() => {
        isHoveredRef.current = false
      }}
    >
      <div ref={trackRef} className="flex flex-nowrap w-max will-change-transform">
        {renderContent(setRef)}
        {renderContent()}
      </div>
    </Link>
  )
}

export default AnniversaryTicker
